import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { doc, getDoc, updateDoc, serverTimestamp } from "firebase/firestore";
import { ref as storageRef, uploadBytes, getDownloadURL } from "firebase/storage";
import { db, storage } from "../../firebase";
import { getFunctions, httpsCallable } from "firebase/functions";
import "./setup-profile.css";

const SetupProfile = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [userData, setUserData] = useState(null);
  
  const [form, setForm] = useState({
    name: "",
    firstName: "",
    lastName: "",
    password: "",
    confirmPassword: "",
    photoUrl: "",
  });
  
  const [photoFile, setPhotoFile] = useState(null);
  const [photoPreview, setPhotoPreview] = useState("");

  const userId = searchParams.get("userId");
  const setupToken = searchParams.get("token");

  useEffect(() => {
    validateAndLoadUser();
  }, [userId, setupToken]);

  const validateAndLoadUser = async () => {
    try {
      console.log("🔍 Validating setup link...");
      console.log("User ID:", userId);
      console.log("Setup Token:", setupToken);

      if (!userId || !setupToken) {
        setError("Invalid setup link. Please contact your recruiter.");
        setLoading(false);
        return;
      }

      // Get user data from Firestore
      console.log("📊 Fetching user from Firestore...");
      const userDoc = await getDoc(doc(db, "users", userId));
      
      console.log("User document exists:", userDoc.exists());
      
      if (!userDoc.exists()) {
        setError(`User not found in database (ID: ${userId}). The link may be incorrect or the user hasn't been created yet. Please contact your recruiter.`);
        setLoading(false);
        return;
      }

      const user = userDoc.data();
      console.log("✅ User data:", { email: user.email, setupToken: user.setupToken, profileSetupComplete: user.profileSetupComplete });

      // Verify setup token matches
      if (user.setupToken !== setupToken) {
        console.log("❌ Token mismatch!");
        console.log("Expected:", user.setupToken);
        console.log("Provided:", setupToken);
        setError("Invalid or expired setup link. Please request a new invitation.");
        setLoading(false);
        return;
      }

      console.log("✅ Token valid!");

      // Check if already set up
      if (user.profileSetupComplete) {
        console.log("⚠️ Profile already set up");
        setError("Profile already set up. Please login.");
        setLoading(false);
        setTimeout(() => navigate("/login"), 3000);
        return;
      }

      console.log("✅ Profile not yet complete, loading form...");

      // Load user data into form
      setUserData(user);
      setForm({
        name: user.name || "",
        firstName: user.firstName || "",
        lastName: user.lastName || "",
        password: "",
        confirmPassword: "",
        photoUrl: user.photoUrl || "",
      });
      setPhotoPreview(user.photoUrl || "");
      setLoading(false);
    } catch (err) {
      console.error("Error loading user:", err);
      setError("Failed to load profile. Please try again.");
      setLoading(false);
    }
  };

  const handlePhotoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        alert("File size must be less than 5MB");
        return;
      }
      
      setPhotoFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setPhotoPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const uploadPhoto = async () => {
    if (!photoFile) return form.photoUrl;

    const fileExt = photoFile.name.split(".").pop();
    const fileName = `profile_${userId}_${Date.now()}.${fileExt}`;
    const photoRef = storageRef(storage, `profiles/${fileName}`);
    
    await uploadBytes(photoRef, photoFile);
    const url = await getDownloadURL(photoRef);
    return url;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    // Validation
    if (!form.firstName.trim() || !form.lastName.trim()) {
      setError("First name and last name are required");
      return;
    }

    if (form.password.length < 6) {
      setError("Password must be at least 6 characters");
      return;
    }

    if (form.password !== form.confirmPassword) {
      setError("Passwords do not match");
      return;
    }

    try {
      setSaving(true);

      // Upload photo if provided
      let photoUrl = form.photoUrl;
      if (photoFile) {
        photoUrl = await uploadPhoto();
      }

      const fullName = `${form.firstName.trim()} ${form.lastName.trim()}`;

      // Call Cloud Function to set password and update profile
      const functions = getFunctions();
      const setupProfile = httpsCallable(functions, "completeProfileSetup");
      
      const result = await setupProfile({
        userId,
        setupToken,
        password: form.password,
        profileData: {
          name: fullName,
          firstName: form.firstName.trim(),
          lastName: form.lastName.trim(),
          fullName: fullName,
          photoUrl: photoUrl,
        },
      });

      if (result.data.success) {
        // Update local Firestore (Cloud Function also updates it, but this ensures consistency)
        await updateDoc(doc(db, "users", userId), {
          name: fullName,
          firstName: form.firstName.trim(),
          lastName: form.lastName.trim(),
          fullName: fullName,
          photoUrl: photoUrl,
          profileSetupComplete: true,
          profileSetupAt: serverTimestamp(),
          setupToken: null, // Invalidate token
          updatedAt: serverTimestamp(),
        });

        // Show success and redirect to login
        alert("✅ Profile setup complete! You can now log in with your email and password.");
        navigate("/login");
      } else {
        throw new Error(result.data.message || "Failed to set up profile");
      }
    } catch (err) {
      console.error("Error setting up profile:", err);
      setError(err.message || "Failed to set up profile. Please try again.");
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="setup-profile-page">
        <div className="setup-card">
          <div className="loading-spinner">
            <div className="spinner"></div>
            <p>Loading your profile...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error && !userData) {
    return (
      <div className="setup-profile-page">
        <div className="setup-card">
          <div className="error-state">
            <div className="error-icon">⚠️</div>
            <h2>Setup Link Issue</h2>
            <p>{error}</p>
            <button onClick={() => navigate("/login")} className="btn-primary">
              Go to Login
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="setup-profile-page">
      <div className="setup-card">
        <div className="setup-header">
          <div className="logo">💎 WEALTH BUILDERS</div>
          <h1>Complete Your Profile</h1>
          <p>Welcome! Let's set up your account.</p>
        </div>

        {error && (
          <div className="error-message">
            ⚠️ {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="setup-form">
          {/* Photo Upload */}
          <div className="form-section">
            <label className="section-label">Profile Picture</label>
            <div className="photo-upload">
              <div className="photo-preview">
                {photoPreview ? (
                  <img src={photoPreview} alt="Preview" />
                ) : (
                  <div className="photo-placeholder">
                    <span className="initials">
                      {form.firstName?.[0]}{form.lastName?.[0]}
                    </span>
                  </div>
                )}
              </div>
              <div className="photo-actions">
                <input
                  type="file"
                  id="photo-input"
                  accept="image/*"
                  onChange={handlePhotoChange}
                  style={{ display: "none" }}
                />
                <label htmlFor="photo-input" className="btn-upload">
                  📷 {photoPreview ? "Change Photo" : "Upload Photo"}
                </label>
                <p className="help-text">JPG, PNG or GIF (max 5MB)</p>
              </div>
            </div>
          </div>

          {/* Name Fields */}
          <div className="form-section">
            <label className="section-label">Your Name</label>
            <div className="form-row">
              <div className="form-group">
                <label>First Name *</label>
                <input
                  type="text"
                  value={form.firstName}
                  onChange={(e) => setForm({ ...form, firstName: e.target.value })}
                  required
                  className="form-input"
                  placeholder="John"
                />
              </div>
              <div className="form-group">
                <label>Last Name *</label>
                <input
                  type="text"
                  value={form.lastName}
                  onChange={(e) => setForm({ ...form, lastName: e.target.value })}
                  required
                  className="form-input"
                  placeholder="Doe"
                />
              </div>
            </div>
          </div>

          {/* Account Info */}
          <div className="form-section">
            <label className="section-label">Account Information</label>
            <div className="info-box">
              <div className="info-row">
                <span className="info-label">Email:</span>
                <span className="info-value">{userData?.email}</span>
              </div>
              {userData?.agencyCode && (
                <div className="info-row">
                  <span className="info-label">Agency Code:</span>
                  <span className="info-value">{userData.agencyCode}</span>
                </div>
              )}
            </div>
          </div>

          {/* Password Fields */}
          <div className="form-section">
            <label className="section-label">Set Your Password</label>
            <div className="form-group">
              <label>Password *</label>
              <input
                type="password"
                value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })}
                required
                minLength="6"
                className="form-input"
                placeholder="At least 6 characters"
              />
            </div>
            <div className="form-group">
              <label>Confirm Password *</label>
              <input
                type="password"
                value={form.confirmPassword}
                onChange={(e) => setForm({ ...form, confirmPassword: e.target.value })}
                required
                minLength="6"
                className="form-input"
                placeholder="Re-enter your password"
              />
            </div>
            <p className="help-text">
              Choose a strong password with at least 6 characters
            </p>
          </div>

          {/* Submit Button */}
          <button
            type="submit"
            className="btn-submit"
            disabled={saving}
          >
            {saving ? (
              <>
                <span className="spinner-small"></span>
                Setting up...
              </>
            ) : (
              <>🚀 Complete Setup</>
            )}
          </button>
        </form>

        <div className="setup-footer">
          <p>Need help? Contact your recruiter or support.</p>
        </div>
      </div>
    </div>
  );
};

export default SetupProfile;

